/**
 * READING CONTENT FROM WHERE IT ACTUALLY LIVES — the edge, by version.
 *
 * Under D1-B the server holds no content. It answers one question — `GET /manifest` → which
 * version, and the base URL its content lives under — and everything heavy is a static file at
 * `<baseUrl>/<version>/<file>`: a Cloudflare edge in production, the server's own dev static
 * mount (same origin) in development. One transport, both places; the manifest is the only call
 * that needs the network, which is also the seam offline hangs on (ARCHITECTURE.md D2).
 *
 * This is the client for that shape. It shares the give-up-aware fetch in `./fetch.ts` and needs
 * no version header: the version is in the URL, so every build is a distinct immutable address a
 * cache may keep for ever.
 */
import { ROUTES, apiUrl, contentUrl } from '@versesverse/schema/api';
import type { ManifestResponse } from '@versesverse/schema/api';
import { ARTIFACTS } from '@versesverse/schema/artifacts';
import type {
  MapOverviewArtifact,
  MapSkeletonArtifact,
  NodeRecordArtifact,
  NodeRefsArtifact,
  ReportsArtifact,
  SearchIndexArtifact,
  VerseChunkArtifact,
} from '@versesverse/schema/artifacts';
import type { Bundle } from '@versesverse/schema/types';
import { requestJson, resolveGiveUp } from './fetch.js';
import type { ApiFetch, GiveUpOptions, ResolvedGiveUp } from './fetch.js';

/**
 * Ask the server which build is live and where its content is.
 *
 * The one call that needs the server. `apiBase` is `''` for the same origin, or the API's origin
 * where the client is served from somewhere else (the static-host case — `VITE_API_BASE`).
 */
export async function fetchManifest(
  apiBase: string,
  fetch: ApiFetch,
  options: GiveUpOptions = {},
): Promise<ManifestResponse> {
  const url = apiUrl(apiBase, ROUTES.manifest.path());
  return requestJson<ManifestResponse>(fetch, url, resolveGiveUp(options));
}

export interface ContentClientOptions extends GiveUpOptions {
  /** Where versioned content lives — `manifest.content.baseUrl`. `''` means same origin. */
  baseUrl: string;
  /** The build to read — `manifest.version`. In the path of every URL, so it cannot be forgotten. */
  version: string;
  fetch: ApiFetch;
}

/**
 * Reads one build's content as static files. Bound to a single version: a new build is a new
 * client, because a client that could silently change which version it reads is a client that
 * could hand a reader two halves of two libraries.
 */
export class ContentClient {
  private readonly baseUrl: string;
  private readonly version: string;
  private readonly doFetch: ApiFetch;
  private readonly give: ResolvedGiveUp;

  constructor(options: ContentClientOptions) {
    this.baseUrl = options.baseUrl;
    this.version = options.version;
    this.doFetch = options.fetch;
    this.give = resolveGiveUp(options);
  }

  /** The map itself: names, stories, differences, references. No verse text. */
  async bundle(): Promise<Bundle> {
    return this.get<Bundle>(ARTIFACTS.bundle());
  }

  /** One range of verse text. Which range a passage is in comes from `Bundle.chunkStarts`. */
  async verseChunk(chunk: number): Promise<VerseChunkArtifact> {
    return this.get<VerseChunkArtifact>(ARTIFACTS.verseChunk(chunk));
  }

  /** One text's search postings. */
  async searchIndex(corpus: string): Promise<SearchIndexArtifact> {
    return this.get<SearchIndexArtifact>(ARTIFACTS.searchIndex(corpus));
  }

  /** One text's per-name verse lists. */
  async nodeRefs(corpus: string): Promise<NodeRefsArtifact> {
    return this.get<NodeRefsArtifact>(ARTIFACTS.nodeRefs(corpus));
  }

  /**
   * One figure's hadith — the reports that name them.
   *
   * Fetched only when a panel is opened. The bundle carries `reportCounts`, a number per
   * figure, so the page can say how many there are without downloading 8.2 MB of Arabic
   * that most readers never look at.
   */
  async reports(node: string, page = 0): Promise<ReportsArtifact> {
    /* Paged since 2026-08-20 — page 0 keeps the original path. See ARTIFACTS.reports for the
       19.2 MB file that made it necessary. */
    return this.get<ReportsArtifact>(ARTIFACTS.reports(node, page));
  }

  /*
   * THE COMPILATION IS NOT HERE, AND IT WAS — found 2026-08-17, before it shipped.
   *
   * Three methods on this class fetched the skeleton, the overview and a node record. They were on the
   * wrong client, and the mistake was invisible because the shapes and the tests were all correct: a
   * `ContentClient` builds `<contentBase>/<version>/<file>`, so every one of them fetched the
   * compilation **from the edge**. That is the precise thing `ARCHITECTURE.md D26` closed — the layer
   * anonymously available from three hundred cities — reintroduced by the class the call happened to
   * sit in.
   *
   * Two more things followed from it and neither looked wrong either. The service worker matched those
   * URLs against `contentBase` first and filed them in the CONTENT cache, so §9's per-build partitions
   * were unreachable in practice. And making the bucket prefix private (`§12` step 5) would have
   * 403'd the app, because the app was reading the public copy.
   *
   * The compilation is fetched through the API by `CompilationClient` below. Same reasoning as the
   * server's `CompilationSource` being separate from `LibraryRepository`: two planes, two clients, and
   * the one that talks to the edge must not be able to reach the one that does not.
   */

  /**
   * The URL a content file is fetched from — `<baseUrl>/<version>/<file>`.
   *
   * Public because a client that fetches has to be able to name what it fetched, for a give-up
   * message and for an offline store's own keys.
   */
  urlFor(file: string): string {
    return contentUrl(this.baseUrl, this.version, file);
  }

  private async get<T>(file: string): Promise<T> {
    return requestJson<T>(this.doFetch, this.urlFor(file), this.give);
  }
}

/**
 * THE COMPILATION, FETCHED THROUGH THE API — `ARCHITECTURE.md D26`, `DELIVERY.md §5`.
 *
 * A separate client from `ContentClient` because it talks to a different place for a different reason.
 * Content is scripture: public, on the edge, addressed `<contentBase>/<version>/<file>`. This is what
 * the project made: answered by the server so that taking it is costly and attributable, addressed
 * `<apiBase>/api/v1/compilation/<version>/…`, and refused with a 409 if the caller names a build the
 * server no longer serves.
 *
 * Three requests instead of one, and only the first is needed to draw anything: the overview opens the
 * app at ~7 kB, the skeleton is every figure the map draws at 34.8 kB, and a record is what a click
 * needs at a median of 0.3 kB. The whole layer in one request is 348 kB today and 6.4 MB by twenty
 * thousand figures, which is the measurement this shape exists for.
 */
export class CompilationClient {
  private readonly apiBase: string;
  private readonly version: string;
  private readonly doFetch: ApiFetch;
  private readonly give: ResolvedGiveUp;

  constructor(options: { apiBase: string; version: string; fetch: ApiFetch } & GiveUpOptions) {
    this.apiBase = options.apiBase;
    this.version = options.version;
    this.doFetch = options.fetch;
    this.give = resolveGiveUp(options);
  }

  /** The zoomed-out view: one glyph per screen cell, bounded by the screen rather than the corpus. */
  async mapOverview(): Promise<MapOverviewArtifact> {
    return this.get<MapOverviewArtifact>(ROUTES.compilationOverview.path(this.version));
  }

  /** Every figure the map draws, as rows, with its edges as indices into them. */
  async mapSkeleton(): Promise<MapSkeletonArtifact> {
    return this.get<MapSkeletonArtifact>(ROUTES.compilationSkeleton.path(this.version));
  }

  /**
   * What one click needs: the half of a gazetteer record the map never draws, and this figure's ties.
   *
   * One round trip and almost no bytes, which is the shape of the cost — so a reader who clicks waits
   * for the network and not for the payload, and prefetching (`DELIVERY.md §4.8`) is what fixes that
   * rather than making the record smaller.
   */
  async nodeRecord(node: string): Promise<NodeRecordArtifact> {
    return this.get<NodeRecordArtifact>(ROUTES.compilationNode.path(this.version, node));
  }

  /** The URL one compilation route is fetched from — `<apiBase>/api/v1/compilation/<version>/…`. */
  urlFor(routePath: string): string {
    return apiUrl(this.apiBase, routePath);
  }

  private async get<T>(routePath: string): Promise<T> {
    return requestJson<T>(this.doFetch, this.urlFor(routePath), this.give);
  }
}

/** A live library: the manifest that was read, and a client bound to the version it names. */
export interface OpenLibrary {
  manifest: ManifestResponse;
  content: ContentClient;
  /**
   * The compilation, through the API (D26) — bound to the same build the content client is.
   *
   * Handed out together so the two can never disagree about which version they are reading. That is
   * not a convenience: the skeleton addresses edges by index into its own rows, so a client holding a
   * skeleton from one build and nodes from another would draw edges between the wrong figures.
   */
  compilation: CompilationClient;
}

/**
 * The whole of getting online: ask the server which build is live, then return a content client
 * bound to it. One call a reader's client makes on load; everything after is static files.
 */
export async function openLibrary(
  apiBase: string,
  fetch: ApiFetch,
  options: GiveUpOptions = {},
): Promise<OpenLibrary> {
  const manifest = await fetchManifest(apiBase, fetch, options);
  const content = new ContentClient({
    baseUrl: manifest.content.baseUrl,
    version: manifest.version,
    fetch,
    ...options,
  });
  /* The compilation goes to the API, not to `manifest.content.baseUrl` — that is the whole of D26 on
     the client side, and putting it on the content client is how it was got wrong the first time. */
  const compilation = new CompilationClient({ apiBase, version: manifest.version, fetch, ...options });
  return { manifest, content, compilation };
}

/**
 * EVERY CONTENT FILE THE CURRENT BUILD SHIPS, as absolute URLs — the bundle, every verse range, and
 * each text's search index and name list. Built from the SAME `ARTIFACTS` table and `urlFor` a
 * client fetches through, so "what is downloaded for offline" and "what is read online" can never
 * name different files. The web's service worker prefetches this list into the Cache API; the phone
 * writes it to device storage — one list, two stores.
 */
export function contentFiles(
  content: Pick<ContentClient, 'urlFor'>,
  bundle: { chunkCount: number; indexManifest?: { corpus: string }[]; refManifest?: { corpus: string }[] },
): string[] {
  const files = [content.urlFor(ARTIFACTS.bundle())];
  for (let c = 0; c < bundle.chunkCount; c++) files.push(content.urlFor(ARTIFACTS.verseChunk(c)));
  for (const manifest of bundle.indexManifest ?? []) files.push(content.urlFor(ARTIFACTS.searchIndex(manifest.corpus)));
  for (const manifest of bundle.refManifest ?? []) files.push(content.urlFor(ARTIFACTS.nodeRefs(manifest.corpus)));
  return files;
}

/**
 * THE URL PREFIX EVERY COMPILATION ROUTE SHARES — `ARCHITECTURE.md D26`.
 *
 * Given to the service worker so its cache decision is an exact prefix match on a base the app
 * supplied, exactly as `contentBase` is. A worker that inferred this from the shape of a URL would be
 * a worker caching whatever else happened to look similar.
 *
 * Built from `ROUTES` rather than written out, so it cannot drift from the routes it is meant to cover:
 * every one of them begins `compilation/`, and the one thing that must stay true is that this prefix
 * matches all of them and nothing else.
 */
export function compilationBase(apiBase: string): string {
  return apiUrl(apiBase, 'compilation/');
}

/**
 * WHAT "SAVE FOR OFFLINE" MUST PULL OF THE COMPILATION, as absolute URLs.
 *
 * The skeleton and the overview — what the map needs to draw itself with no network. Built from the
 * same `ROUTES` table the online path asks through, for the same reason `contentFiles` is built from
 * `ARTIFACTS`: what is downloaded and what is read must not be able to name different things.
 *
 * THE NODE RECORDS ARE DELIBERATELY NOT HERE, and that is a scoping decision rather than an oversight.
 * There are 1,099 of them and a reader who saves for offline has asked for the library, not for every
 * figure's prose — `DELIVERY.md §10` makes scoped download the open refinement and §4.9 records that a
 * whole-layer fetch is right in exactly one place, which is a download the reader asked for. Until that
 * scope exists, a saved library opens the map and a figure opened offline shows what the map knows.
 */
export function compilationFiles(apiBase: string, version: string): string[] {
  return [
    apiUrl(apiBase, ROUTES.compilationSkeleton.path(version)),
    apiUrl(apiBase, ROUTES.compilationOverview.path(version)),
  ];
}
